import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { Definition, Environment } from '../types';
import { useNotification } from '../contexts/NotificationContext';
import { useModal } from '../contexts/ModalContext';

// Language mapping: display name -> backend value
const LANGUAGE_MAPPING: Record<string, string> = {
  "Python": "python",
  "Java": "java",
  "C#": "csharp",
  "TypeScript": "ts",
  "PHP": "php",
  "Ruby": "ruby",
  "C++": "cpp",
};

// Supported languages for protobuf compilation (using display names)
const SUPPORTED_LANGUAGES: Record<string, boolean> = {
  "Python": true,
  "Java": true,
  "C#": true,
  "TypeScript": true,
  "PHP": true,
  "Ruby": true,
  "C++": true,
};

// Get file extension for each language (using display names)
const getFileExtensionForLanguage = (language: string): string => {
  const extensions: Record<string, string> = {
    "Python": "py",
    "Java": "java",
    "C#": "cs",
    "TypeScript": "ts",
    "PHP": "php",
    "Ruby": "rb",
    "C++": "cpp",
  };
  return extensions[language] || "txt";
};

// Helper function to get backend value from display name
const getBackendLanguageValue = (displayName: string): string => {
  return LANGUAGE_MAPPING[displayName] || displayName.toLowerCase();
};

// Helper function to download a blob as a file
const downloadBlob = (blob: Blob, filename: string): void => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

const DefinitionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { openModal, closeModal } = useModal();
  const [definition, setDefinition] = useState<Definition | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [downloadingProto, setDownloadingProto] = useState(false);
  const [downloadingDefinition, setDownloadingDefinition] = useState(false);
  
  // Publish modal states
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  
  // Data for title resolution
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [dataBuilds, setDataBuilds] = useState<any[]>([]);
  const [ancestorsExpanded, setAncestorsExpanded] = useState(false);

  // Download compiled classes states
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [downloadingCompiledClasses, setDownloadingCompiledClasses] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [languageSearchTerm, setLanguageSearchTerm] = useState('');
  
  // Refs for dropdowns
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      loadDefinition();
    }
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadDefinition = async () => {
    try {
      setLoading(true);
      const data = await networkManager.getDefinition(id!);
      setDefinition(data);
      
      // Load definitions if there are ancestors to resolve their titles
      if (data.ancestors && data.ancestors.length > 0) {
        await loadDefinitions();
      }
      
      // Load the .txt file content if definitionURL exists
      if (data.definitionURL) {
        await loadFileContent(data.definitionURL);
      }
    } catch (err) {
      console.error('Error loading definition:', err);
      showNotification('Failed to load definition', 'error', 8000);
    } finally {
      setLoading(false);
    }
  };

  const loadFileContent = async (url: string) => {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const content = await response.text();
        setFileContent(content);
      } else {
        setFileContent('Unable to load file content');
      }
    } catch (err) {
      console.error('Error loading file content:', err);
      setFileContent('Error loading file content');
    }
  };

  const formatDate = (dateString: string) => {
    // Parse the date string and treat it as UTC
    const date = new Date(dateString);
    
    // Get UTC components to avoid timezone conversion issues
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    
    return `${day}/${month}/${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleDownloadProto = async () => {
    if (!definition) return;
    
    try {
      setDownloadingProto(true);
      await networkManager.downloadProtoFile(definition._id, definition.title);
      showNotification(`Proto file for "${definition.title}" downloaded successfully`, 'success', 5000);
    } catch (error: any) {
      console.error('Error downloading proto file:', error);
      showNotification(
        error.message || 'Failed to download proto file',
        'error',
        8000
      );
    } finally {
      setDownloadingProto(false);
    }
  };

  const handleDownloadDefinition = async () => {
    if (!definition || !definition.definitionURL) {
      showNotification('No definition file URL available', 'error', 5000);
      return;
    }
    
    try {
      setDownloadingDefinition(true);
      
      // Fetch the file from the URL
      const response = await fetch(definition.definitionURL);
      
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create filename from the definition title
      const sanitizedTitle = definition.title.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_');
      
      // Try to get the original filename from the URL or use a default
      const urlPath = definition.definitionURL.split('/').pop();
      const originalFilename = urlPath && urlPath.includes('.') ? urlPath : `${sanitizedTitle}.txt`;
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = originalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      showNotification(`Definition file "${originalFilename}" downloaded successfully`, 'success', 5000);
    } catch (error: any) {
      console.error('Error downloading definition file:', error);
      showNotification(
        error.message || 'Failed to download definition file',
        'error',
        8000
      );
    } finally {
      setDownloadingDefinition(false);
    }
  };

  const handleDownloadCompiledClasses = async () => {
    if (!definition || !selectedLanguage) return;
    
    try {
      setDownloadingCompiledClasses(true);
      
      const orgId = networkManager.getOrganizationId();
      if (!orgId) throw new Error('Organization ID not found');
      
      const currentToken = localStorage.getItem('authToken');
      if (!currentToken) throw new Error('No authentication token found');
      
      const backendLanguageValue = getBackendLanguageValue(selectedLanguage);
      const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api'}/TLOG/definitions/${definition._id}/compiled/${backendLanguageValue}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': currentToken,
        },
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to get compiled classes: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to get compiled classes: ${response.statusText}`);
        }
      }

      const contentType = response.headers.get('content-type');
      
      // Check if the response is source code (text content)
      if (contentType && contentType.includes('text/')) {
        const sourceCode = await response.text();
        const fileExtension = getFileExtensionForLanguage(selectedLanguage);
        const filename = `compiled_classes_${selectedLanguage}.${fileExtension}`;
        
        // Download as source code file
        const blob = new Blob([sourceCode], { type: 'text/plain' });
        downloadBlob(blob, filename);
        
        showNotification(`Source code for ${selectedLanguage.toUpperCase()} downloaded successfully`, 'success', 5000);
      } else {
        // Download as ZIP file
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `compiled_classes_${selectedLanguage}.zip`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const blob = await response.blob();
        
        if (blob.size === 0) {
          throw new Error(`No files were generated for ${selectedLanguage.toUpperCase()}. The downloaded file is empty.`);
        }
        
        downloadBlob(blob, filename);
        
        showNotification(`Compiled classes for ${selectedLanguage.toUpperCase()} downloaded successfully`, 'success', 5000);
      }
      
      // Clear the dropdown selection after successful download
      setSelectedLanguage('');
      setLanguageSearchTerm('');
      setShowLanguageDropdown(false);
    } catch (error: any) {
      console.error('Error downloading compiled classes:', error);
      showNotification(
        `Error downloading ${selectedLanguage.toUpperCase()} classes: ${error.message || 'Failed to download compiled classes'}`,
        'error',
        8000
      );
    } finally {
      setDownloadingCompiledClasses(false);
    }
  };

  // Language dropdown handlers
  const handleLanguageSelect = (language: string) => {
    setSelectedLanguage(language);
    setLanguageSearchTerm(language.charAt(0).toUpperCase() + language.slice(1));
    setShowLanguageDropdown(false);
  };

  const handleLanguageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLanguageSearchTerm(value);
    setShowLanguageDropdown(true);
    if (!value) {
      setSelectedLanguage('');
    }
  };

  const handleLanguageInputFocus = () => setShowLanguageDropdown(true);

  const clearLanguageSelection = () => {
    setSelectedLanguage('');
    setLanguageSearchTerm('');
    setShowLanguageDropdown(false);
  };

  // Filter languages based on search term
  const filteredLanguages = Object.keys(SUPPORTED_LANGUAGES).filter(lang =>
    lang.toLowerCase().includes(languageSearchTerm.toLowerCase())
  );

  const loadEnvironments = async () => {
    try {
      console.log('🔍 Starting to load environments...');
      setLoadingEnvironments(true);
      const envs = await networkManager.getEnvironments();
      console.log('✅ Environments loaded successfully:', envs);
      setEnvironments(envs);
      
      // Load definitions and data builds for title resolution
      await Promise.all([
        loadDefinitions(),
        loadDataBuilds()
      ]);
    } catch (err) {
      console.error('❌ Error loading environments:', err);
      setPublishError('Failed to load environments');
    } finally {
      setLoadingEnvironments(false);
    }
  };

  const loadDefinitions = async () => {
    try {
      const data = await networkManager.getDefinitions();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading definitions:', err);
      setDefinitions([]);
    }
  };

  const loadDataBuilds = async () => {
    try {
      const data = await networkManager.getDataBuilds();
      const successfulDataBuilds = Array.isArray(data) 
        ? data.filter(build => build.status === 'success')
        : [];
      setDataBuilds(successfulDataBuilds);
    } catch (err) {
      console.error('Error loading data builds:', err);
      setDataBuilds([]);
    }
  };

  // Helper functions to get titles by ID
  const getDefinitionTitle = (definitionId: string) => {
    const definition = definitions.find(def => def._id === definitionId);
    return definition ? definition.title : definitionId;
  };

  const getGamedataTitle = (gamedataId: string) => {
    const gamedata = dataBuilds.find(build => build._id === gamedataId);
    return gamedata ? gamedata.title : gamedataId;
  };

  const handlePublish = async () => {
    if (!selectedEnvironment || !definition) return;
    
    try {
      setPublishing(true);
      setPublishError(null);
      
      // Check if environment requires password - use ID instead of name
      // Get the actual environment ID from the environments list
      const actualEnvironment = environments.find(env => env._id === selectedEnvironment._id || env.name === selectedEnvironment.name);
      if (!actualEnvironment || !actualEnvironment._id) {
        throw new Error('Environment not found or invalid');
      }
      const environmentId = actualEnvironment._id;
      
      // Validate that we have a proper ID (not a name)
      if (!environmentId || environmentId === selectedEnvironment.name) {
        throw new Error(`Invalid environment ID. Expected ID but got: ${environmentId}`);
      }
      
      const envDetails = await networkManager.getEnvironmentDetailsById(environmentId);
      
      // Check if deploymentPassword exists and is not empty
      const hasPassword = envDetails.environmentConfigs?.deploymentPassword && 
                         envDetails.environmentConfigs.deploymentPassword.trim() !== '';
      
      if (hasPassword) {
        // Show password modal
        const PasswordModalContent = () => {
          const [password, setPassword] = useState('');
          const [validating, setValidating] = useState(false);
          
          const handlePasswordSubmit = async () => {
            if (!password.trim()) return;
            
            try {
              setValidating(true);
              
              // Attempt to update with password - use the environmentId we already validated
              await networkManager.updateEnvironment(environmentId, {
                DefinitionID: definition._id,
                deploymentPassword: password
              });
              
              // Success
              closeModal('password-required');
              setSelectedEnvironment(null);
              setPublishError(null);
              
              showNotification(
                `Successfully published definition to environment: ${selectedEnvironment.name}`,
                'success',
                5000
              );
            } catch (err) {
              // Password incorrect or other error
              closeModal('password-required');
              const errorMessage = err instanceof Error ? err.message : 'Failed to publish definition';
              
              if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('incorrect')) {
                showNotification(
                  `Incorrect Password for Environment ${selectedEnvironment.name}.`,
                  'error',
                  5000
                );
              } else {
                setPublishError(errorMessage);
              }
            } finally {
              setValidating(false);
            }
          };
          
          const handleCancel = () => {
            closeModal('password-required');
          };
          
          return (
            <div className="space-y-6">
              <div>
                <label htmlFor="deployment-password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="deployment-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && password.trim() && !validating) {
                      handlePasswordSubmit();
                    }
                  }}
                  placeholder="Enter environment password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  disabled={validating}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  CANCEL
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  disabled={!password.trim() || validating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {validating ? 'Validating...' : 'OK'}
                </button>
              </div>
            </div>
          );
        };
        
        openModal({
          id: 'password-required',
          title: `Environment ${selectedEnvironment.name} Requires a Password`,
          content: <PasswordModalContent />,
          size: 'md'
        });
        
        setPublishing(false);
        return;
      }
      
      // No password required - proceed normally - use the environmentId we already validated
      await networkManager.updateEnvironment(environmentId, {
        DefinitionID: definition._id
      });
      
      setSelectedEnvironment(null);
      
      // Show success notification using toast
      showNotification(
        `Successfully published definition to environment: ${selectedEnvironment.name}`,
        'success',
        5000
      );
    } catch (err) {
      console.error('Error publishing definition:', err);
      setPublishError(err instanceof Error ? err.message : 'Failed to publish definition');
    } finally {
      setPublishing(false);
    }
  };

  const openPublishModal = async () => {
    console.log('🚀 Opening publish modal...');
    console.log('🔗 Backend URL:', process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api (default)');
    console.log('🏢 Organization ID:', networkManager.getOrganizationId());
    
    try {
      setPublishError(null);
      setSelectedEnvironment(null);
      setLoadingEnvironments(true);
      
      // Load environments directly and get fresh data
      const orgId = networkManager.getOrganizationId();
      if (!orgId) {
        throw new Error('Organization ID not found');
      }
      
      console.log('🔍 Loading environments for organization:', orgId);
      const envs = await networkManager.getEnvironments();
      console.log('✅ Environments loaded successfully:', envs);
      
      // Also load definitions and data builds for title resolution
      const [defs, builds] = await Promise.all([
        networkManager.getDefinitions(),
        networkManager.getDataBuilds()
      ]);
      
      const definitions = Array.isArray(defs) ? defs : [];
      const dataBuilds = Array.isArray(builds) 
        ? builds.filter(build => build.status === 'success')
        : [];
      
      // Helper functions to get titles by ID (local to this function)
      const getDefinitionTitle = (definitionId: string) => {
        const definition = definitions.find(def => def._id === definitionId);
        return definition ? definition.title : definitionId;
      };

      const getGamedataTitle = (gamedataId: string) => {
        const gamedata = dataBuilds.find(build => build._id === gamedataId);
        return gamedata ? gamedata.title : gamedataId;
      };
      
      // Create a React component for the modal content that can handle its own state
      const PublishModalContent = () => {
        const [localSelectedEnvironment, setLocalSelectedEnvironment] = useState<Environment | null>(null);
        
        const handlePublish = async () => {
          if (!localSelectedEnvironment || !definition) return;
          
          try {
            setPublishing(true);
            setPublishError(null);
            
            // Ensure we're using the ID, not the name
            // Get the actual environment ID from the environments list
            const actualEnvironment = envs.find(env => env._id === localSelectedEnvironment._id || env.name === localSelectedEnvironment.name);
            if (!actualEnvironment || !actualEnvironment._id) {
              throw new Error('Environment not found or invalid');
            }
            
            // Use the actual ID from the environment object
            const environmentId = actualEnvironment._id;
            
            // Validate that we have a proper ID (not a name)
            if (!environmentId || environmentId === localSelectedEnvironment.name) {
              throw new Error(`Invalid environment ID. Expected ID but got: ${environmentId}`);
            }
            
            // Check if environment requires password - use ID instead of name
            const envDetails = await networkManager.getEnvironmentDetailsById(environmentId);
            
            // Check if deploymentPassword exists and is not empty
            const hasPassword = envDetails.environmentConfigs?.deploymentPassword && 
                               envDetails.environmentConfigs.deploymentPassword.trim() !== '';
            
            if (hasPassword) {
              // Show password modal
              const PasswordModalContent = () => {
                const [password, setPassword] = useState('');
                const [validating, setValidating] = useState(false);
                
                const handlePasswordSubmit = async () => {
                  if (!password.trim()) return;
                  
                  try {
                    setValidating(true);
                    
                    // Attempt to update with password - use the environmentId we already validated
                    await networkManager.updateEnvironment(environmentId, {
                      DefinitionID: definition._id,
                      deploymentPassword: password
                    });
                    
                    // Success - close password modal and publish modal
                    closeModal('password-required');
                    closeModal('publish-definition');
                    setSelectedEnvironment(null);
                    setPublishError(null);
                    
                    showNotification(
                      `Successfully published definition to environment: ${localSelectedEnvironment.name}`,
                      'success',
                      5000
                    );
                  } catch (err) {
                    // Password incorrect or other error
                    closeModal('password-required');
                    const errorMessage = err instanceof Error ? err.message : 'Failed to publish definition';
                    
                    if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('incorrect')) {
                      showNotification(
                        `Incorrect Password for Environment ${localSelectedEnvironment.name}.`,
                        'error',
                        5000
                      );
                    } else {
                      setPublishError(errorMessage);
                    }
                  } finally {
                    setValidating(false);
                  }
                };
                
                const handleCancel = () => {
                  closeModal('password-required');
                };
                
                return (
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="deployment-password" className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        id="deployment-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && password.trim() && !validating) {
                            handlePasswordSubmit();
                          }
                        }}
                        placeholder="Enter environment password"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={handleCancel}
                        disabled={validating}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        CANCEL
                      </button>
                      <button
                        onClick={handlePasswordSubmit}
                        disabled={!password.trim() || validating}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {validating ? 'Validating...' : 'OK'}
                      </button>
                    </div>
                  </div>
                );
              };
              
              openModal({
                id: 'password-required',
                title: `Environment ${localSelectedEnvironment.name} Requires a Password`,
                content: <PasswordModalContent />,
                size: 'md'
              });
              
              setPublishing(false);
              return;
            }
            
            // No password required - proceed normally - use the environmentId we already validated
            await networkManager.updateEnvironment(environmentId, {
              DefinitionID: definition._id
            });
            
            // Show success notification
            showNotification(
              `Successfully published definition to environment: ${localSelectedEnvironment.name}`,
              'success',
              5000
            );
            
            // Close modal and reset states
            closeModal('publish-definition');
            setSelectedEnvironment(null);
            setPublishError(null);
            
          } catch (err) {
            console.error('Error publishing definition:', err);
            setPublishError(err instanceof Error ? err.message : 'Failed to publish definition');
          } finally {
            setPublishing(false);
          }
        };
        
        return (
          <div className="space-y-6">
            {publishError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {publishError}
              </div>
            )}
            
            {envs.length === 0 ? (
              <div className="text-center py-8">
                <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-gray-500">No environments available</p>
                <p className="text-sm text-gray-400 mt-1">Create an environment first to publish definition</p>
              </div>
            ) : (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select Environment
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {envs.map((env) => (
                    <label
                      key={env._id}
                      className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-colors ${
                        localSelectedEnvironment?._id === env._id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="environment"
                        value={env._id}
                        checked={localSelectedEnvironment?._id === env._id}
                        onChange={() => setLocalSelectedEnvironment(env)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-semibold text-gray-900 text-lg">{env.name}</div>
                          {localSelectedEnvironment?._id === env._id && (
                            <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        
                        {env.description && (
                          <div className="text-sm text-gray-600 mb-4">{env.description}</div>
                        )}
                        
                        <div className="space-y-3">
                          {/* Definition Section */}
                          <div className="bg-gray-50 rounded-md p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">Definition</span>
                            </div>
                            {env.publishedDefinitionID ? (
                              <div className="text-sm">
                                <div className="text-gray-900 font-medium">Published</div>
                                <a 
                                  href={`/definitions/${env.publishedDefinitionID}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 hover:underline text-xs block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {getDefinitionTitle(env.publishedDefinitionID)}
                                  <svg className="h-3 w-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                                {env.definitionPublisher && (
                                  <div className="text-gray-500 text-xs mt-1">
                                    by {env.definitionPublisher.name}
                                  </div>
                                )}
                                {env.publishedDefinitionID === definition?._id && (
                                  <div className="text-blue-600 text-xs mt-1 font-medium">
                                    ✓ This is the current definition
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">No definition published</div>
                            )}
                          </div>
                          
                          {/* Gamedata Section */}
                          <div className="bg-gray-50 rounded-md p-3">
                            <div className="flex items-center space-x-2 mb-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">Gamedata</span>
                            </div>
                            {env.publishedDataID ? (
                              <div className="text-sm">
                                <div className="text-gray-900 font-medium">Published</div>
                                <a 
                                  href={`/data-builds/${env.publishedDataID}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 hover:underline text-xs block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {getGamedataTitle(env.publishedDataID)}
                                  <svg className="h-4 w-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                                {env.dataPublisher && (
                                  <div className="text-blue-600 text-xs mt-1">
                                    by {env.dataPublisher.name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">No gamedata published</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  closeModal('publish-definition');
                  setSelectedEnvironment(null);
                  setPublishError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={!localSelectedEnvironment || publishing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {publishing ? (
                  <>
                    <div className="spinner h-4 w-4 mr-2"></div>
                    Publishing...
                  </>
                ) : (
                  'Publish'
                )}
              </button>
            </div>
          </div>
        );
      };
      
      const publishModalContent = <PublishModalContent />;
      
      // Update state with fresh data
      setEnvironments(envs);
      setDefinitions(definitions);
      setDataBuilds(dataBuilds);
      
      // Open modal with fresh content
      openModal({
        id: 'publish-definition',
        title: 'Publish Definition',
        content: publishModalContent,
        size: 'xl',
        closeOnOverlayClick: false
      });
      
    } catch (error) {
      console.error('❌ Error loading environments:', error);
      setPublishError(error instanceof Error ? error.message : 'Failed to load environments');
      
      // Show error modal
      const errorModalContent = (
        <div className="space-y-4">
          <div className="text-center py-4">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-red-600 font-medium">Error Loading Environments</p>
            <p className="text-gray-600 text-sm mt-1">Failed to load environments. Please try again.</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => closeModal('error-modal')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      );
      
      openModal({
        id: 'error-modal',
        title: 'Error',
        content: errorModalContent,
        size: 'md',
        closeOnOverlayClick: true
      });
    } finally {
      setLoadingEnvironments(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading definition...</div>
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-lg text-gray-600 mb-4">Definition not found</div>
            <button
              onClick={() => navigate('/definitions')}
              className="btn btn-secondary"
            >
              Back to Definitions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/definitions')}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>
            
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">{definition.title}</h1>
              {definition.deprecated && (
                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  Deprecated
                </span>
              )}
            </div>
          </div>

          {/* Minimalist Elegant Layout */}
          <div className="flex items-center space-x-6">
            {/* Status Indicator */}
            <div className="flex items-center space-x-3">
              <div className={`w-2.5 h-2.5 rounded-full ${
                definition.deprecated ? 'bg-yellow-500' : 'bg-green-500'
              }`} />
              <span className={`text-sm font-medium ${
                definition.deprecated ? 'text-yellow-700' : 'text-green-700'
              }`}>
                {definition.deprecated ? 'Deprecated' : 'Active'}
              </span>
              {!definition.deprecated && (
                <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-300" />

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={openPublishModal}
                disabled={definition?.deprecated}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  !definition?.deprecated
                    ? 'text-blue-700 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 hover:border-blue-300'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title={definition?.deprecated ? 'Deprecated definitions cannot be published' : 'Publish to environment'}
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Publish
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Definition Info */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Definition Info</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <p className="text-sm text-gray-900">{definition.title}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-sm text-gray-900">{definition.description || 'No description provided'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                <div className="flex items-center space-x-2">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p className="text-sm text-gray-900">{definition.author?.name || 'Unknown'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <div className="flex items-center">
                  {definition.deprecated ? (
                    <div className="flex items-center text-yellow-600">
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Deprecated
                    </div>
                  ) : (
                    <div className="flex items-center text-green-600">
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Active
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
                <p className="text-sm text-gray-900">{formatDate(definition.createdAt)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Updated At</label>
                <p className="text-sm text-gray-900">{formatDate(definition.updatedAt)}</p>
              </div>

              {definition.definitionURL && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File URL</label>
                  <a 
                    href={definition.definitionURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                  >
                    {definition.definitionURL}
                  </a>
                </div>
              )}

              {/* Ancestors Section */}
              {definition.ancestors && definition.ancestors.length > 0 && (
                <div>
                  <button
                    onClick={() => setAncestorsExpanded(!ancestorsExpanded)}
                    className="flex items-center justify-between w-full text-left mb-2 group"
                  >
                    <label className="block text-sm font-medium text-gray-700">
                      Based on the following definitions ({definition.ancestors.length})
                    </label>
                    <svg 
                      className={`h-4 w-4 text-gray-500 transition-transform duration-200 group-hover:text-gray-700 ${
                        ancestorsExpanded ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {ancestorsExpanded && (
                    <div className="space-y-2">
                      {definition.ancestors.map((ancestorId) => {
                        const ancestorDefinition = definitions.find(def => def._id === ancestorId);
                        return (
                          <div 
                            key={ancestorId} 
                            className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors cursor-pointer"
                            onClick={() => navigate(`/definitions/${ancestorId}`)}
                          >
                            <svg className="h-4 w-4 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-blue-700 truncate">
                                {getDefinitionTitle(ancestorId)}
                              </div>
                              {ancestorDefinition && (
                                <p className="text-xs text-gray-500 truncate mt-1">
                                  {ancestorDefinition.description || 'No description'}
                                </p>
                              )}
                            </div>
                            <svg className="h-4 w-4 text-blue-500 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* File Content */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            {/* Title */}
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Definition Content</h2>
            
            {/* Download Buttons */}
            <div className="flex items-center space-x-3 mb-6">
              <button
                onClick={handleDownloadDefinition}
                disabled={downloadingDefinition || !definition?.definitionURL}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  !downloadingDefinition && definition?.definitionURL
                    ? 'text-gray-700 hover:text-gray-800 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title={!definition?.definitionURL ? 'No definition file URL available' : 'Download Definition File'}
              >
                {downloadingDefinition ? (
                  <svg className="h-4 w-4 animate-spin mr-1.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                {downloadingDefinition ? 'Downloading...' : 'Download Definition'}
              </button>
              <button
                onClick={handleDownloadProto}
                disabled={downloadingProto}
                className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  !downloadingProto
                    ? 'text-blue-700 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 hover:border-blue-300'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title="Download Proto File"
              >
                {downloadingProto ? (
                  <svg className="h-4 w-4 animate-spin mr-1.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                {downloadingProto ? 'Downloading...' : 'Download Proto'}
              </button>
              
              {/* Integrated Compiled Classes Dropdown */}
              <div className="relative" ref={languageDropdownRef}>
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={languageSearchTerm}
                      onChange={handleLanguageInputChange}
                      onFocus={handleLanguageInputFocus}
                      className="input text-sm w-32"
                      placeholder="Language..."
                      autoComplete="off"
                    />
                    
                    {selectedLanguage && (
                      <button
                        type="button"
                        onClick={clearLanguageSelection}
                        className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    
                    <div className="absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    
                    {showLanguageDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredLanguages.length > 0 ? (
                          filteredLanguages.map((lang) => (
                            <div
                              key={lang}
                              onClick={() => handleLanguageSelect(lang)}
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900">
                                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                                </span>
                                {selectedLanguage === lang && (
                                  <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No languages found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={handleDownloadCompiledClasses}
                    disabled={!selectedLanguage || downloadingCompiledClasses}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                      selectedLanguage && !downloadingCompiledClasses
                        ? 'text-green-700 hover:text-green-800 hover:bg-green-50 border border-green-200 hover:border-green-300'
                        : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                    }`}
                    title={!selectedLanguage ? 'Please select a language first' : 'Download compiled classes'}
                  >
                    {downloadingCompiledClasses ? (
                      <>
                        <svg className="h-4 w-4 animate-spin mr-1.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Classes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {fileContent ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
                  {fileContent}
                </pre>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500">No file content available</p>
                {!definition.definitionURL && (
                  <p className="text-sm text-gray-400 mt-1">No file URL provided</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};

export default DefinitionDetailsPage;
